/**
 * Agency branding — stored at /users/{uid}/agency/branding.
 *
 * One doc per agent today (single-agent assumption). When we introduce
 * multi-agent agencies, we'll hoist this to /agencies/{agencyId}/branding
 * and link users.agencyId → agencies/{id}.
 *
 * All fields are nullable at the type level so partial saves work (autosave
 * while the user is still filling things in). The UI layer is responsible
 * for surfacing "required for publishing" gaps.
 */

import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  Timestamp,
  type Unsubscribe,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { auth, db, storage } from "./firebase";

export type TypographyPairing =
  | "editorial"
  | "modern"
  | "tech";

export const TYPOGRAPHY_PAIRINGS: Record<
  TypographyPairing,
  { label: string; sub: string; sans: string; serif: string }
> = {
  editorial: {
    label: "Éditorial",
    sub: "Inter + Instrument Serif",
    sans: "Inter",
    serif: "Instrument Serif",
  },
  modern: {
    label: "Moderne",
    sub: "DM Sans + DM Serif Display",
    sans: "DM Sans",
    serif: "DM Serif Display",
  },
  tech: {
    label: "Tech",
    sub: "Space Grotesk + IBM Plex Serif",
    sans: "Space Grotesk",
    serif: "IBM Plex Serif",
  },
};

export const ACCENT_PRESETS: { id: string; label: string; hex: string }[] = [
  { id: "lime", label: "Lime", hex: "#D9EA2C" },
  { id: "forest", label: "Forêt", hex: "#2F5D3A" },
  { id: "brick", label: "Brique", hex: "#C2552A" },
  { id: "ocean", label: "Océan", hex: "#1F4E8C" },
  { id: "rose", label: "Rose", hex: "#E8A3B8" },
  { id: "ink", label: "Encre", hex: "#14161A" },
];

export type Agency = {
  name: string | null;
  baseline: string | null;
  logoUrl: string | null;
  logoPath: string | null; // storage path — tracked so we can delete-replace
  address: string | null;
  siret: string | null;
  carteT: string | null;
  rcs: string | null;
  rcPro: string | null;
  garantieFinanciere: string | null;
};

export type Palette = {
  accentHex: string;
};

export type Typography = {
  pairing: TypographyPairing;
};

export type AgentSignature = {
  photoUrl: string | null;
  photoPath: string | null;
  name: string | null;
  rsac: string | null;
  phone: string | null;
  email: string | null;
  tagline: string | null;
};

export type Branding = {
  agency: Agency;
  palette: Palette;
  typography: Typography;
  agent: AgentSignature;
  updatedAt: Timestamp | null;
};

export const DEFAULT_BRANDING: Branding = {
  agency: {
    name: null,
    baseline: null,
    logoUrl: null,
    logoPath: null,
    address: null,
    siret: null,
    carteT: null,
    rcs: null,
    rcPro: null,
    garantieFinanciere: null,
  },
  palette: { accentHex: "#D9EA2C" },
  typography: { pairing: "editorial" },
  agent: {
    photoUrl: null,
    photoPath: null,
    name: null,
    rsac: null,
    phone: null,
    email: null,
    tagline: null,
  },
  updatedAt: null,
};

// ─── Paths ─────────────────────────────────────────────────────────────────
function brandingRef(uid: string) {
  return doc(db, "users", uid, "agency", "branding");
}

// ─── Read (live) ───────────────────────────────────────────────────────────
export function subscribeBranding(
  cb: (b: Branding) => void,
): Unsubscribe {
  const user = auth.currentUser;
  if (!user) {
    cb(DEFAULT_BRANDING);
    return () => {};
  }
  return onSnapshot(brandingRef(user.uid), (snap) => {
    if (!snap.exists()) {
      cb(DEFAULT_BRANDING);
      return;
    }
    cb(mergeWithDefaults(snap.data() as Partial<Branding>));
  });
}

export async function getBranding(): Promise<Branding> {
  const user = auth.currentUser;
  if (!user) return DEFAULT_BRANDING;
  const snap = await getDoc(brandingRef(user.uid));
  if (!snap.exists()) return DEFAULT_BRANDING;
  return mergeWithDefaults(snap.data() as Partial<Branding>);
}

function mergeWithDefaults(partial: Partial<Branding>): Branding {
  return {
    agency: { ...DEFAULT_BRANDING.agency, ...(partial.agency ?? {}) },
    palette: { ...DEFAULT_BRANDING.palette, ...(partial.palette ?? {}) },
    typography: {
      ...DEFAULT_BRANDING.typography,
      ...(partial.typography ?? {}),
    },
    agent: { ...DEFAULT_BRANDING.agent, ...(partial.agent ?? {}) },
    updatedAt: partial.updatedAt ?? null,
  };
}

// ─── Write ─────────────────────────────────────────────────────────────────
// Full-doc writes — the doc is tiny (<2 KB), so we don't bother with
// field-level merges.
export async function saveBranding(b: Branding): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in");
  await setDoc(brandingRef(user.uid), {
    ...b,
    updatedAt: serverTimestamp(),
  });
}

// ─── Uploads ───────────────────────────────────────────────────────────────
export type UploadKind = "logo" | "agent-photo";

// Deterministic path + timestamp suffix — cache-bust via the URL on every
// new upload, and delete the prior object once we have the new URL.
export async function uploadImage(
  kind: UploadKind,
  file: File,
): Promise<{ url: string; path: string }> {
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in");
  const ext = (file.name.split(".").pop() ?? "png").toLowerCase();
  const path = `users/${user.uid}/uploads/${kind}-${Date.now()}.${ext}`;
  const r = storageRef(storage, path);
  await uploadBytes(r, file, { contentType: file.type });
  const url = await getDownloadURL(r);
  return { url, path };
}

export async function deleteImage(path: string): Promise<void> {
  if (!path) return;
  try {
    await deleteObject(storageRef(storage, path));
  } catch {
    // Ignore — object may already be gone; nothing to clean up.
  }
}

// ─── Color utilities ───────────────────────────────────────────────────────
export function normalizeHex(input: string): string | null {
  const v = input.trim().toLowerCase();
  const m = v.match(/^#?([0-9a-f]{6}|[0-9a-f]{3})$/);
  if (!m) return null;
  const h = m[1];
  if (h.length === 3) {
    return "#" + h.split("").map((c) => c + c).join("");
  }
  return "#" + h;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(hexA: string, hexB: string): number {
  const la = relativeLuminance(hexA);
  const lb = relativeLuminance(hexB);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

// Returns a black/white foreground that reads best on the given background.
export function readableForeground(bgHex: string): "#14161A" | "#FFFFFF" {
  return relativeLuminance(bgHex) > 0.55 ? "#14161A" : "#FFFFFF";
}

// Extract the dominant color of an image by sampling its pixels on a small
// canvas and bucketing into a coarse histogram. Skips near-white / near-black
// so we don't return "white background" for logos with transparent bg.
// Returns null if we can't read the image (CORS, odd format, tiny).
export async function extractDominantColor(
  file: File,
): Promise<string | null> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const canvas = document.createElement("canvas");
    const w = 64;
    const scale = w / Math.max(img.width, 1);
    canvas.width = w;
    canvas.height = Math.max(1, Math.round(img.height * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const buckets = new Map<string, { r: number; g: number; b: number; n: number }>();
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];
      if (a < 128) continue;
      const maxC = Math.max(r, g, b);
      const minC = Math.min(r, g, b);
      // skip near-white, near-black, and near-gray
      if (maxC > 240 && minC > 220) continue;
      if (maxC < 20) continue;
      if (maxC - minC < 12) continue;
      const key = `${Math.round(r / 24)}-${Math.round(g / 24)}-${Math.round(b / 24)}`;
      const cur = buckets.get(key);
      if (cur) {
        cur.r += r;
        cur.g += g;
        cur.b += b;
        cur.n += 1;
      } else {
        buckets.set(key, { r, g, b, n: 1 });
      }
    }
    let best: { r: number; g: number; b: number; n: number } | null = null;
    for (const v of buckets.values()) {
      if (!best || v.n > best.n) best = v;
    }
    if (!best) return null;
    const r = Math.round(best.r / best.n);
    const g = Math.round(best.g / best.n);
    const b = Math.round(best.b / best.n);
    return (
      "#" +
      [r, g, b]
        .map((c) => c.toString(16).padStart(2, "0"))
        .join("")
    ).toUpperCase();
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

// ─── Legal completeness — for "ready to publish" badge ────────────────────
export function brandingReadiness(b: Branding): {
  ready: boolean;
  missing: string[];
} {
  const missing: string[] = [];
  if (!b.agency.name?.trim()) missing.push("Nom de l'agence");
  if (!b.agency.address?.trim()) missing.push("Adresse");
  if (!b.agency.siret?.trim()) missing.push("SIRET");
  if (!b.agency.carteT?.trim()) missing.push("Carte T");
  if (!b.agency.rcPro?.trim()) missing.push("RC Pro");
  if (!b.agent.name?.trim()) missing.push("Nom de l'agent");
  if (!b.agent.email?.trim()) missing.push("Email agent");
  return { ready: missing.length === 0, missing };
}
